import type {
  ComposeLaunchProofInput,
  InternalGovernanceViewV1,
  InternalReportViewV1,
  StudioDepartureKitStatusV1,
} from "@enrein/shared-contracts";
import type { StudioApiClient } from "./api.js";

function departureKitSummary(status: StudioDepartureKitStatusV1): string {
  const download = status.downloadAvailable ? "Download available." : "Download not available yet.";
  return `Departure Kit status: ${status.issuanceStatus}. ${download}`;
}

export interface StudioLaunchProofApiSnapshot {
  readonly report: InternalReportViewV1 | null;
  readonly governance: InternalGovernanceViewV1 | null;
  readonly departureKit: StudioDepartureKitStatusV1 | null;
}

export async function loadStudioLaunchProofApiSnapshot(
  api: StudioApiClient,
  projectId: string,
): Promise<StudioLaunchProofApiSnapshot> {
  const [report, governance, departureKit] = await Promise.all([
    api.getCurrentInternalReport(projectId).catch(() => null),
    api.getGovernance(projectId).catch(() => null),
    api.getDepartureKitStatus(projectId).catch(() => null),
  ]);

  return { report, governance, departureKit };
}

export function buildLaunchProofInputFromStudioApis(
  projectId: string,
  snapshot: StudioLaunchProofApiSnapshot,
  generatedAt: string = new Date().toISOString(),
): ComposeLaunchProofInput {
  const { report, governance, departureKit } = snapshot;

  return {
    phase1Results: undefined,
    proofReferenceLabel: `Launch proof · ${projectId}`,
    generatedAt,
    agencyId: report?.agencyId ?? governance?.agencyId,
    projectId: report?.projectId ?? governance?.projectId ?? projectId,
    businessGraphRevisionId: report?.businessGraphRevisionId,
    artifactSetHash: report?.artifactSetHash,
    searchReadinessHeadline: report?.headline,
    searchReadinessQaStatus: report?.qaStatus,
    searchReadinessCoverage: report?.coverage,
    searchReadinessStaleBanner: report?.staleBanner ?? null,
    qualityGateStatus: report?.qaStatus,
    departureKitSummary: departureKit ? departureKitSummary(departureKit) : governance?.dkStatusSummary,
    managerExceptions: governance?.managerExceptions.map(
      (exception) => `${exception.checkAreaLabel}: ${exception.impactSummary}`,
    ),
    skippedChecks: governance?.coverage.downgradeReasons,
    clientMissingInfo: governance?.clientResponsibilityTrail.items.map((item) => item.clientSafeSummary),
  };
}
